import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  OkedSelectTrigger,
  SelectOption,
  TextField,
} from '@app/components';
import * as yup from 'yup';
import { useForm } from '../hooks';
import { useTranslation } from 'react-i18next';
import { Oked, Portfolio } from '@app/models';
import { get } from 'http';

interface PortfolioFormProps {
  onSubmit: (data: Partial<Portfolio>, files?: File[]) => void;
  onCancel: () => void;
  pending: boolean;
  portfolio?: Portfolio | null;
}

interface PortfolioData {
  name: string;
  description: string;
  position: string;
  startDate: Date | null;
  endDate: Date | null;
  okedCode: string;
  okedName: string;
  companyPhone: string;
  id?: string;
}

const getToday = () => {
  return new Date(new Date().toISOString().split('T')[0]);
};

function PortfolioForm(props: PortfolioFormProps) {
  const { onSubmit, pending, onCancel, portfolio: portfolioProps } = props;
  const { t } = useTranslation();
  const { values, onChange, errors, setErrors, validate, setValues } =
    useForm<PortfolioData>({
      name: '',
      description: '',
      position: '',
      startDate: null,
      endDate: null,
      companyPhone: '',
      okedCode: '',
      okedName: '',
    });

  useEffect(() => {
    if (portfolioProps) {
      setValues({
        id: portfolioProps.id,
        name: portfolioProps?.name,
        description: portfolioProps?.description,
        position: portfolioProps?.position,
        startDate: new Date(portfolioProps?.startDate),
        endDate: new Date(portfolioProps?.endDate),
        companyPhone: portfolioProps?.companyPhone,
        okedCode: portfolioProps?.okedCode,
        okedName: portfolioProps?.okedCode,
      });
    }
  }, []);

  const [checked, setChecked] = useState(false);

  const validateDateFields = useCallback(async () => {
    let hasErrors = false;
    let settableErrors = {
      ...errors,
      startDate: null as string | null,
      endDate: null as string | null,
    };
    if (!values.endDate) {
      settableErrors = {
        ...settableErrors,
        endDate: t('required', { ns: 'validation' })!,
      };
      hasErrors = true;
    }
    if (!values.startDate) {
      settableErrors = {
        ...settableErrors,
        startDate: t('required', { ns: 'validation' })!,
      };
      hasErrors = true;
    }

    setErrors(settableErrors);

    return hasErrors;
  }, [errors, setErrors, t, values.endDate, values.startDate]);

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const trimmedName = values.name.trim();
      const trimmedPosition = values.position.trim();
      const trimmedDescription = values.description.trim();

      if (
        trimmedName !== values.name ||
        trimmedPosition !== values.position ||
        trimmedDescription !== values.description
      ) {
        onChange(trimmedName, 'name');
        onChange(trimmedPosition, 'position');
        onChange(trimmedDescription, 'description');
      }

      const hasErrors = await Promise.all([
        validate(
          yup.object().shape({
            name: yup
              .string()
              .required(t('required', { ns: 'validation' })!)
              .test(
                'min-chars',
                'Минимум 2 символа',
                (val) => (val || '').replace(/\s/g, '').length >= 2
              )
              .test(
                'no-special-chars',
                'Спецсимволы запрещены',
                (val) => !/[~`!@#$%^&*_+=[\]{}|\\;:'"<>/?]+/.test(val || '')
              ),
            description: yup
              .string()
              .required(t('required', { ns: 'validation' })!)
              .test(
                'min-chars',
                'Минимум 2 символа',
                (val) => (val || '').replace(/\s/g, '').length >= 2
              )
              .test(
                'no-special-chars',
                'Спецсимволы запрещены',
                (val) => !/[~`!@#$%^&*_+=[\]{}|\\;:'"<>/?]+/.test(val || '')
              ),
            position: yup
              .string()
              .required(t('required', { ns: 'validation' })!)
              .test(
                'min-chars',
                'Минимум 2 символа',
                (val) => (val || '').replace(/\s/g, '').length >= 2
              )
              .test(
                'no-special-chars',
                'Спецсимволы запрещены',
                (val) => !/[~`!@#$%^&*_+=[\]{}|\\;:'"<>/?]+/.test(val || '')
              ),
            startDate: yup
              .string()
              .required(t('required', { ns: 'validation' })!),
            endDate: yup
              .string()
              .required(t('required', { ns: 'validation' })!),
            okedCode: yup
              .string()
              .required(t('required', { ns: 'validation' })!),
            companyPhone: yup
              .string()
              .required(t('required', { ns: 'validation' })!),
          })
        ),
        validateDateFields(),
      ]);

      if (hasErrors[0] || hasErrors[1]) {
        return;
      }
      const { okedName, ...otherValues } = values;
      onSubmit({
        ...otherValues,
        startDate: values.startDate?.toJSON(),
        endDate: values.endDate?.toJSON(),
      });
    },
    [values, validate, t, validateDateFields, onSubmit]
  );

  const onChangeOked = useCallback(
    (data: SelectOption<Oked>) => {
      onChange(data.value.nameRu, 'okedName');
      onChange(data.value.code, 'okedCode');
    },
    [onChange]
  );

  const onChangeCheckbox = useCallback(
    (value: boolean) => {
      setChecked(value);
      if (value) onChange(getToday(), 'endDate');
      else onChange(null, 'endDate');
    },
    [onChange]
  );

  return (
    <form onSubmit={onSubmitForm} className="space-y-4">
      <div className="w-full">
        <TextField
          label="Наименование компании(заказчика)"
          placeholder="Введите наименование"
          name="name"
          value={values.name}
          onChange={onChange}
          helperText={errors.name}
          error={!!errors.name}
          editable={!pending}
          required
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <OkedSelectTrigger
          onChange={onChangeOked}
          label="Категория"
          name="oked"
          value={values.okedName || undefined}
          placeholder="Выберите сферу деятельности"
          helperText={errors.okedCode || undefined}
          error={!!errors.okedCode}
        />
        <TextField
          label="Должность"
          placeholder="Введите должность"
          name="position"
          value={values.position}
          onChange={onChange}
          helperText={errors.position}
          error={!!errors.position}
          editable={!pending}
          required
        />
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 z-0">
          <div className="flex-1">
            <DatePicker
              value={values.startDate}
              name="startDate"
              onChange={onChange}
              label="Начало работы"
              disabled={pending}
              helperText={errors.startDate}
              error={!!errors.startDate}
              maxDate={values.endDate || getToday()}
              required
            />
          </div>

          <div className="flex justify-center sm:justify-start">
            <Checkbox
              label="По текущее время"
              checked={checked}
              onChange={onChangeCheckbox}
              disabled={pending}
            />
          </div>

          <div className="flex-1">
            <DatePicker
              value={values.endDate}
              name="endDate"
              onChange={onChange}
              label="Завершение работы"
              disabled={pending || checked}
              helperText={errors.endDate}
              error={!!errors.endDate}
              minDate={values.startDate || undefined}
              required
            />
          </div>
        </div>
      </div>

      <div className="w-full">
        <TextField
          label="Контакты компании"
          onChange={onChange}
          name="companyPhone"
          value={values.companyPhone || ''}
          mask="(###) ### ## ##"
          placeholder="(777) 777 77 77"
          helperText={errors.companyPhone}
          error={!!errors.companyPhone}
          editable={!pending}
          required
        />
      </div>

      <div className="w-full">
        <TextField
          label="Описание работы"
          placeholder="Введите описание"
          name="description"
          value={values.description}
          onChange={onChange}
          helperText={errors.description}
          error={!!errors.description}
          editable={!pending}
          required
          multiline
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-4 pt-4">
        <Button
          text="Отменить"
          type="button"
          onClick={onCancel}
          disabled={pending}
          loading={pending}
          color="default"
          className="w-full sm:w-auto"
        />
        <Button
          text="Принять"
          type="submit"
          disabled={pending}
          loading={pending}
          className="w-full sm:w-auto"
        />
      </div>
    </form>
  );
}

export default memo(PortfolioForm);
